import { useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { FormField } from '@/components/molecules/FormField';
import { Button } from '@/components/atoms/Button';
import { ApiError } from '@/services/api/client';
import { authAPI } from '@/services/api/auth.api';
import { TokenService } from '@/services/auth/TokenService';
import { useAuth } from '@/hooks/useAuth';
import { createChangeEmailSchema, type ChangeEmailFormData } from '@/utils/validation/auth.schemas';
import { cn } from '@/utils/cn';
import type { ChangeEmailFormProps, ChangeEmailLabels } from './ChangeEmailForm.types';
import styles from './ChangeEmailForm.module.scss';

const DEFAULT_LABELS: ChangeEmailLabels = {
  title: 'Change email',
  newEmailLabel: 'New email',
  newEmailPlaceholder: 'Enter your new email',
  helper: 'We will use this email to sign in.',
  submit: 'Update email',
  success: 'Your email has been updated.',
  emailInUse: 'That email is already in use.',
  genericError: 'Could not update the email. Please try again.',
  validation: {
    emailRequired: 'Enter your new email.',
    emailInvalid: 'Enter a valid email address.',
  },
};

/** Etiquetas en español, para áreas sin i18n (p. ej. backoffice). */
export const CHANGE_EMAIL_LABELS_ES: ChangeEmailLabels = {
  title: 'Cambiar correo electrónico',
  newEmailLabel: 'Nuevo correo electrónico',
  newEmailPlaceholder: 'Introduce tu nuevo correo',
  helper: 'Usarás este correo para iniciar sesión.',
  submit: 'Actualizar correo',
  success: 'Tu correo se ha actualizado.',
  emailInUse: 'Ese correo ya está en uso.',
  genericError: 'No se pudo actualizar el correo. Inténtalo de nuevo.',
  validation: {
    emailRequired: 'Introduce tu nuevo correo.',
    emailInvalid: 'Introduce un correo electrónico válido.',
  },
};

export function ChangeEmailForm({ labels, className }: ChangeEmailFormProps) {
  const l = labels ?? DEFAULT_LABELS;
  const { updateUser } = useAuth();
  const schema = useMemo(() => createChangeEmailSchema(l.validation), [l.validation]);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<ChangeEmailFormData>({ resolver: zodResolver(schema) });

  const [serverError, setServerError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const onSubmit = async (data: ChangeEmailFormData) => {
    setServerError(null);
    setSuccess(false);
    try {
      const res = await authAPI.changeEmail({ newEmail: data.newEmail });
      // Reemitido por el backend: refrescamos token y usuario en la sesión.
      TokenService.setToken(res.token);
      updateUser(res.user);
      setSuccess(true);
      reset();
    } catch (err) {
      if (err instanceof ApiError && err.status === 409) {
        setServerError(l.emailInUse);
      } else {
        setServerError(l.genericError);
      }
    }
  };

  return (
    <form className={cn(styles.form, className)} onSubmit={handleSubmit(onSubmit)} noValidate>
      <FormField
        label={l.newEmailLabel}
        type="email"
        placeholder={l.newEmailPlaceholder}
        helperText={l.helper}
        autoComplete="email"
        error={errors.newEmail?.message}
        {...register('newEmail')}
      />

      {serverError && (
        <p className={styles.form__error} role="alert">
          {serverError}
        </p>
      )}
      {success && (
        <p className={styles.form__success} role="status">
          {l.success}
        </p>
      )}

      <div className={styles.form__actions}>
        <Button type="submit" variant="primary" isLoading={isSubmitting}>
          {l.submit}
        </Button>
      </div>
    </form>
  );
}

export default ChangeEmailForm;
